import { type SiteId } from '@solinkcorp/api-types';
import {
  canManageAlarmSettings,
  useUserHasFeature,
  useUserSession,
} from '@solinkcorp/authentication';
import { Button, Flex, toast } from '@solinkcorp/solink-components';
import { CallhomeApi } from 'clients';
import { type IEvent } from 'clients/PlatformApi';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import useLocale from 'src/hooks/useLocale';
import { getAlarmProviderType } from 'utils/AlarmUtil';
import { type VideoDiscoverySite } from 'utils/types';
import { ALARM_PROVIDER_TYPES } from 'vd-constants';
import AlarmEventActions from './AlamEventActions/AlarmEventActions';
import { useAlarmConfigForLocation } from './hooks/useAlarmConfiguration';
interface AlarmEventActionsRendererProps {
  event: IEvent;
  location: VideoDiscoverySite;
  onClose: () => void;
}

function AlarmEventActionsRenderer(props: AlarmEventActionsRendererProps) {
  const { alarmConfig } = useAlarmConfigForLocation(props.location);
  const user = useUserSession();
  const canVerifyAlarmEvents = useUserHasFeature('enableAlarmVerification');
  const showClaimActions =
    canVerifyAlarmEvents &&
    alarmConfig &&
    getAlarmProviderType(alarmConfig) === ALARM_PROVIDER_TYPES.MONITOR_CENTER;

  const canAccessAlarmActions = useUserHasFeature('armDisarmAlarm') || canManageAlarmSettings(user);

  if (!canAccessAlarmActions) {
    return null;
  }

  if (showClaimActions && !props.event.details?.isAccessRequestEvent) {
    return <AlarmEventClaimAction event={props.event} siteId={props.location.id} />;
  }

  return <AlarmEventActions {...props} />;
}

function AlarmEventClaimAction({ event, siteId }: { event: IEvent; siteId: SiteId }) {
  const locale = useLocale();
  const navigate = useNavigate();
  const [isClaiming, setIsClaiming] = React.useState(false);
  const user = useUserSession();
  const eventId = event.id;
  const verifyAlarmEvent = async () => {
    try {
      setIsClaiming(true);
      const alarmEvent = await CallhomeApi(user.accessToken).editAlarmEvent({
        eventId,
        siteId,
        payload: {},
      });

      navigate(`/verification/alarm/${eventId}`, {
        state: {
          initialVerificationState: alarmEvent.details?.verificationState,
          verifier: alarmEvent.details?.verifier,
        },
      });
    } catch (error) {
      console.error(error);
      toast.error(locale.alarms.verifyError);
    } finally {
      setIsClaiming(false);
    }
  };

  const verifier = event.details?.verifier as string;

  const verifierEmail = verifier
    ? verifier.substring(verifier.lastIndexOf('(') + 1, verifier.lastIndexOf(')'))
    : undefined;

  const userEmail = user.profile.email;

  const currentUserHasBegunVerification = verifierEmail === userEmail;

  const [verifyText, verifyTestId] =
    event.details?.verificationState === 'done'
      ? [locale.verificationCentre.viewVerification, 'view-verification-button']
      : currentUserHasBegunVerification
        ? [locale.verificationCentre.continueVerification, 'continue-verification-button']
        : [locale.alarms.verify, 'verify-button'];

  return (
    <Flex className="footer-actions-wrapper alarm-actions" justifyContent="center">
      <Button
        className="clip-button"
        colorMode={'line-dark'}
        onClick={verifyAlarmEvent}
        size="full"
        text={verifyText}
        dataTestId={verifyTestId}
        disabled={isClaiming}
      />
    </Flex>
  );
}

export default AlarmEventActionsRenderer;
